import type { IconName } from '@cbhq/cds-common';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { formatAuditLogTimestamp } from '../utils/auditLogDisplay';

export type AuditEditDetailRowProps = {
  eventId: string;
  eventIconName: IconName;
  eventTitle: string;
  eventSubtitle?: string;
  timestamp: string;
};

const messages = defineMessages({
  rowAccessibilityLabel: {
    id: 'Activity.auditEditDetailRow.rowAccessibilityLabel',
    defaultMessage: '{eventTitle} on {date} at {time}',
    description: 'Accessibility label for an audit log edit detail row',
  },
});

export function AuditEditDetailRow({
  eventIconName,
  eventTitle,
  eventSubtitle,
  timestamp,
}: AuditEditDetailRowProps) {
  const { formatMessage } = useIntl();
  const { date, time } = formatAuditLogTimestamp(timestamp);

  return (
    <ListCell
      spacingVariant="condensed"
      priority="end"
      media={<Icon name={eventIconName} size="s" color="fg" />}
      titleNode={
        <Text as="span" font="label1" color="fg">
          {eventTitle}
        </Text>
      }
      description={eventSubtitle}
      end={
        <VStack alignItems="flex-end">
          <Text as="span" font="label2" color="fg">
            {date}
          </Text>
          <Text as="span" font="label2" color="fgMuted">
            {time}
          </Text>
        </VStack>
      }
      testID="audit-edit-detail-row"
      accessibilityLabel={formatMessage(messages.rowAccessibilityLabel, {
        eventTitle,
        date,
        time,
      })}
    />
  );
}
